import { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faPhone,
  faEnvelope,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import RoomCalculator from "./RoomCalculator";

const ServiceWidget = ({ widgetId }) => {
  const [isError, setIsError] = useState(false);
  const [widget, setWidget] = useState(null);
  const [surfaceMode, setSurfaceMode] = useState("simple");
  const [directSurface, setDirectSurface] = useState("");
  const [rooms, setRooms] = useState([{ name: "", length: 0, width: 0 }]);
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [clientInfo, setClientInfo] = useState({});
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    const fetchWidget = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/widgets/service/getOneWidget/${widgetId}`
        );
        if (response.status === 200) {
          setWidget(response.data.widget);
        }
      } catch (error) {
        setIsError(true);
      }
    };
    fetchWidget();
  }, [widgetId]);

  const totalSurface =
    surfaceMode === "simple"
      ? parseFloat(directSurface) || 0
      : rooms.reduce((total, room) => total + room.length * room.width, 0);

  const addOnCost = widget
    ? (widget.addOns || []).reduce((total, addOn, index) => {
        if (!selectedAddOns[index]) return total;
        if (addOn.priceType === "per_m2") {
          return total + addOn.price * totalSurface;
        }
        return total + addOn.price;
      }, 0)
    : 0;

  const priceMin = widget
    ? totalSurface * widget.pricePerM2Min + addOnCost
    : 0;
  const priceMax = widget
    ? totalSurface * widget.pricePerM2Max + addOnCost
    : 0;

  const handleAddOnToggle = (index) => {
    setSelectedAddOns((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleInputChange = (e) => {
    setClientInfo((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const selectedAddOnsList = (widget.addOns || [])
      .filter((_, i) => selectedAddOns[i])
      .map((a) =>
        a.priceType === "per_m2"
          ? `${a.name}: ${a.price}€/m² x ${totalSurface.toFixed(1)}m² = ${(a.price * totalSurface).toFixed(2)}€`
          : `${a.name}: ${a.price}€ (forfait)`
      )
      .join("\n");

    const emailBody = `Bonjour,\n\nJe souhaiterais obtenir un devis pour le service "${widget.serviceName}".\n\nNom : ${clientInfo.lastName || ""}\nPrénom : ${clientInfo.firstName || ""}\nTéléphone : ${clientInfo.phone || ""}\nEmail : ${clientInfo.email || ""}\n\nSurface : ${totalSurface.toFixed(1)} m²\n${selectedAddOnsList ? `\nOptions :\n${selectedAddOnsList}` : ""}\n\nEstimation : ${priceMin.toFixed(2)}€ — ${priceMax.toFixed(2)}€\n\nMessage :\n${clientInfo.message || "—"}\n\nCordialement,\n${clientInfo.firstName || ""} ${clientInfo.lastName || ""}`;
    window.location.href = `mailto:${widget.contactMail}?subject=Devis ${widget.serviceName}&body=${encodeURIComponent(emailBody)}`;
  };

  if (isError) {
    return (
      <div className="flex items-center justify-center h-screen w-screen bg-neutral-950 text-white">
        <div className="text-center p-8">
          <p className="text-xl font-semibold mb-2">Simulateur désactivé</p>
          <p className="text-sm text-neutral-400">
            Contactez{" "}
            <a href="https://local.fr" className="underline hover:text-white">
              Local.fr
            </a>{" "}
            pour plus d'informations.
          </p>
        </div>
      </div>
    );
  }

  if (!widget) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-5 h-5 border-2 border-neutral-300 border-t-neutral-800 rounded-full animate-spin" />
      </div>
    );
  }

  const bg = widget?.backgroundColor || "#404040";
  const btn = widget?.btnColor || "#404040";
  const lbl = widget?.labelColor || "#ffffff";

  const inputCls =
    "w-full px-3 py-2.5 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-900 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:bg-white transition-all";

  return (
    <div className="flex flex-col w-full max-h-[100dvh] sm:max-h-[90vh] bg-white sm:rounded-2xl overflow-hidden" style={{ boxShadow: "0 8px 40px rgba(0,0,0,0.12)" }}>

      {/* ── Header: compact, une ligne ── */}
      <div className="flex items-center justify-between px-4 py-3 sm:px-5" style={{ backgroundColor: bg }}>
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate" style={{ color: lbl }}>{widget.title}</p>
          <p className="text-[11px] opacity-60 truncate" style={{ color: lbl }}>{widget.serviceName}</p>
        </div>
        {widget.contactPhone && !showForm && (
          <a
            href={`tel:${widget.contactPhone}`}
            className="shrink-0 ml-3 w-8 h-8 rounded-full flex items-center justify-center text-xs transition-opacity hover:opacity-80"
            style={{ backgroundColor: "rgba(255,255,255,0.15)", color: lbl }}
          >
            <FontAwesomeIcon icon={faPhone} />
          </a>
        )}
      </div>

      {/* ── Body ── */}
      <div className="flex-1 overflow-y-auto styled-scrollbar">
        {!showForm ? (
          <div className="p-4 sm:p-5 flex flex-col gap-5">

            {/* Estimation – toujours visible en haut quand surface > 0 */}
            {widget.priceActive && totalSurface > 0 && (
              <div className="rounded-xl p-4 text-center" style={{ backgroundColor: bg }}>
                <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5 opacity-50" style={{ color: lbl }}>Estimation</p>
                <p className="text-2xl sm:text-3xl font-extrabold tracking-tight leading-none" style={{ color: lbl }}>
                  {priceMin.toFixed(0)}€ <span className="text-lg font-normal opacity-40 mx-1">—</span> {priceMax.toFixed(0)}€
                </p>
                <p className="text-[11px] mt-1.5 opacity-40" style={{ color: lbl }}>pour {totalSurface.toFixed(1)} m²</p>
              </div>
            )}

            {/* Surface */}
            <div>
              <p className="text-[10px] font-bold text-neutral-600 uppercase tracking-widest mb-2.5">Surface à traiter</p>

              {/* Toggle */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setSurfaceMode("simple")}
                  className={`flex items-center gap-2.5 px-3.5 py-3 rounded-xl border-2 transition-all ${
                    surfaceMode !== "simple"
                      ? "border-neutral-300 bg-neutral-50 text-neutral-700 hover:border-neutral-500 hover:bg-neutral-100"
                      : "shadow-md"
                  }`}
                  style={surfaceMode === "simple" ? { backgroundColor: btn, color: lbl, borderColor: btn } : {}}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={surfaceMode === "simple" ? { backgroundColor: "rgba(255,255,255,0.2)" } : { backgroundColor: "#e5e5e5" }}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-bold leading-tight">m² direct</p>
                    <p className="text-[10px] leading-tight" style={{ opacity: surfaceMode === "simple" ? 0.6 : 1, color: surfaceMode === "simple" ? lbl : "#737373" }}>Saisir la surface</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setSurfaceMode("pieces")}
                  className={`flex items-center gap-2.5 px-3.5 py-3 rounded-xl border-2 transition-all ${
                    surfaceMode !== "pieces"
                      ? "border-neutral-300 bg-neutral-50 text-neutral-700 hover:border-neutral-500 hover:bg-neutral-100"
                      : "shadow-md"
                  }`}
                  style={surfaceMode === "pieces" ? { backgroundColor: btn, color: lbl, borderColor: btn } : {}}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                    style={surfaceMode === "pieces" ? { backgroundColor: "rgba(255,255,255,0.2)" } : { backgroundColor: "#e5e5e5" }}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-bold leading-tight">Par pièces</p>
                    <p className="text-[10px] leading-tight" style={{ opacity: surfaceMode === "pieces" ? 0.6 : 1, color: surfaceMode === "pieces" ? lbl : "#737373" }}>Calculer par pièce</p>
                  </div>
                </button>
              </div>

              {surfaceMode === "simple" ? (
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    placeholder="0"
                    className="w-full py-3 px-4 pr-14 bg-neutral-50 border border-neutral-200 rounded-xl text-xl sm:text-2xl font-bold text-neutral-900 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:bg-white transition-all"
                    value={directSurface}
                    onChange={(e) => setDirectSurface(e.target.value)}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-neutral-600">m²</span>
                </div>
              ) : (
                <RoomCalculator rooms={rooms} setRooms={setRooms} btnColor={btn} labelColor={lbl} />
              )}
            </div>

            {/* Options */}
            {widget.addOns && widget.addOns.length > 0 && (
              <div>
                <p className="text-[10px] font-bold text-neutral-600 uppercase tracking-widest mb-2.5">Options</p>
                <div className="flex flex-col gap-1.5">
                  {widget.addOns.map((addOn, index) => {
                    const active = !!selectedAddOns[index];
                    return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleAddOnToggle(index)}
                        className={`flex items-center justify-between w-full px-3.5 py-3 rounded-xl text-left transition-all ${
                          active ? "text-white" : "bg-neutral-50 text-neutral-800 hover:bg-neutral-100"
                        }`}
                        style={active ? { backgroundColor: btn } : {}}
                      >
                        <div className="flex items-center gap-2.5">
                          <div
                            className={`w-[18px] h-[18px] rounded-md flex items-center justify-center shrink-0 transition-all ${
                              active ? "bg-white/25" : "border-2 border-neutral-300"
                            }`}
                          >
                            {active && (
                              <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span className="text-sm font-medium">{addOn.name}</span>
                        </div>
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md ${active ? "bg-white/20" : "bg-neutral-200 text-neutral-700"}`}>
                          {addOn.priceType === "per_m2" ? `${addOn.price}€/m²` : `${addOn.price}€`}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* CTA */}
            <button
              className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
              style={{ backgroundColor: btn, color: lbl }}
              onClick={() => setShowForm(true)}
            >
              Demander un devis
              <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
            </button>
          </div>
        ) : (
          /* ── Formulaire contact ── */
          <form onSubmit={handleFormSubmit} className="p-4 sm:p-5 flex flex-col gap-4">
            <button
              type="button"
              className="self-start flex items-center gap-1.5 text-xs font-semibold text-neutral-600 hover:text-neutral-900 transition-colors mb-1"
              onClick={() => setShowForm(false)}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Retour
            </button>

            {/* Recap mini */}
            {widget.priceActive && totalSurface > 0 && (
              <div className="rounded-xl p-3 flex items-center justify-between" style={{ backgroundColor: bg }}>
                <span className="text-xs font-medium opacity-60" style={{ color: lbl }}>{totalSurface.toFixed(1)} m²</span>
                <span className="text-base font-extrabold" style={{ color: lbl }}>
                  {priceMin.toFixed(0)}€ — {priceMax.toFixed(0)}€
                </span>
              </div>
            )}

            <p className="text-[10px] font-bold text-neutral-600 uppercase tracking-widest">Vos coordonnées</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <div>
                <label className="text-[11px] font-semibold text-neutral-700 mb-1 block">Nom *</label>
                <input type="text" name="lastName" placeholder="Dupont" className={inputCls} value={clientInfo.lastName || ""} onChange={handleInputChange} required />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-neutral-700 mb-1 block">Prénom *</label>
                <input type="text" name="firstName" placeholder="Jean" className={inputCls} value={clientInfo.firstName || ""} onChange={handleInputChange} required />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <div>
                <label className="text-[11px] font-semibold text-neutral-700 mb-1 block">Téléphone *</label>
                <input type="tel" name="phone" placeholder="06 12 34 56 78" className={inputCls} value={clientInfo.phone || ""} onChange={handleInputChange} required />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-neutral-700 mb-1 block">Email *</label>
                <input type="email" name="email" placeholder="votre@email.com" className={inputCls} value={clientInfo.email || ""} onChange={handleInputChange} required />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-neutral-700 mb-1 block">Message</label>
              <textarea
                name="message"
                placeholder="Décrivez votre projet..."
                className={`${inputCls} min-h-[80px] resize-none`}
                value={clientInfo.message || ""}
                onChange={handleInputChange}
              />
            </div>

            <div className="flex gap-2 mt-1">
              <button
                type="submit"
                className="flex-1 py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
                style={{ backgroundColor: btn, color: lbl }}
              >
                <FontAwesomeIcon icon={faEnvelope} />
                Envoyer par email
              </button>
            </div>

            {widget.contactPhone && (
              <a
                href={`tel:${widget.contactPhone}`}
                className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-neutral-700 border border-neutral-200 hover:bg-neutral-50 transition-all"
              >
                <FontAwesomeIcon icon={faPhone} className="text-xs" />
                {widget.contactPhone}
              </a>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default ServiceWidget;
